import React, { useState, useEffect } from "react";
import { Text, Logo, Icon } from 'basis';
import { connect, useDispatch } from "react-redux";
import { Navigate, useSearchParams } from 'react-router-dom';

import TrainingModule from '../components/TrainingModule';
import Divider from "../components/Divider";
import { updateUserDetails } from '../slice/UserSlice';
import { getClicked, getTrainingStatus } from '../functions/TrainingFunctions';
import { resolveModule } from '../functions/trainingModules';
import { logout } from '../functions/UserFunctions';

const mapStateToProps = (state) => ({
    authDetails: state.user.authDetails,
    trainingStatus: state.training.trainingStatus
});

const mapDispatchToProps = { getClicked, getTrainingStatus, logout };

const SECTION_TITLES = {
    'compliance-AU': 'AU Compliance Training',
    'compliance-NZ': 'NZ Compliance Training',
    training1: 'Compliance Training',
    assistedApply: 'Assisted Apply Training'
};

// Training is the combined landing page reached from the Merchant SPA. It shows
// every training the user must complete on a single page: the region-aware
// compliance training (default AU) plus the Assisted Apply training when the
// user's merchant has assisted apply enabled. Each training renders as an
// independent TrainingModule section with its own links, quiz and submit.
function Training(props) {
    const [redirect, setRedirect] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [searchParams] = useSearchParams();

    const region = (searchParams.get('region') || 'AU').toUpperCase();
    const assistedApplyEnabled = searchParams.get('assistedApply') === 'true';
    const merchantId = searchParams.get('merchantId') || '';

    const complianceModule = resolveModule('compliance', region);
    const modules = [complianceModule];
    if (assistedApplyEnabled){
        modules.push('assistedApply');
    }

    // A module is pre-ticked only when UMS (the source of truth) reports it as
    // not required, i.e. complete and still valid against the re-certification
    // watermark. Without a status response nothing is pre-ticked.
    const isModuleComplete = (module) => {
        const status = props.trainingStatus;
        if (!status){
            return false;
        }
        if (module === 'assistedApply'){
            return status.assistedApplyRequired === false;
        }
        return status.complianceRequired === false;
    };

    const dispatch = useDispatch();
    if (!props.authDetails){
        dispatch(updateUserDetails(JSON.parse(window.sessionStorage.getItem('authDetails'))));
    }

    const { authDetails, getClicked, getTrainingStatus } = props;
    useEffect(() => {
        function handleResize(){
            setWidth(window.innerWidth);
        }
        window.addEventListener('resize', handleResize);

        //auth expired or logged out
        if (!authDetails && !window.sessionStorage.getItem('authDetails')){
            setRedirect(true);
        }

        //load shared, module-keyed state once for the whole page
        if (authDetails && authDetails.authToken){
            getClicked(authDetails.authToken);
            getTrainingStatus(authDetails.authToken, merchantId);
        }

        return function removeListener(){
            window.removeEventListener('resize', handleResize);
        };
    }, [authDetails, getClicked, getTrainingStatus, merchantId]);

    const renderName = () => {
        let firstname = `${props.authDetails.firstName.charAt(0).toUpperCase()}${props.authDetails.firstName.slice(1)}`;
        let lastname = props.authDetails.lastName ? `${props.authDetails.lastName.charAt(0).toUpperCase()}${props.authDetails.lastName.slice(1)}` : '';

        return <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginRight: 20}}>
            <Icon name="person" color="primary.blue.t100" />
            <Text as="h6" textStyle="heading6" color="primary.blue.t100">
                {`Hi, ${firstname} ${lastname}`}
            </Text>
        </div>;
    };

    return (
        <div style={{minHeight: window.innerHeight, backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\' viewBox=\'0 0 907.087 907.087\'%3E%3Cdefs%3E%3ClinearGradient id=\'linear-gradient\' x1=\'0.5\' y1=\'0.006\' x2=\'0.5\' y2=\'1.993\' gradientUnits=\'objectBoundingBox\'%3E%3Cstop offset=\'0\' stop-color=\'%23f2eeea\'/%3E%3Cstop offset=\'0.537\' stop-color=\'%23fff\'/%3E%3C/linearGradient%3E%3CclipPath id=\'clip-path\'%3E%3Crect id=\'Rectangle_2\' data-name=\'Rectangle 2\' width=\'907.087\' height=\'907.087\' transform=\'translate(0 3796.621)\' fill=\'url(%23linear-gradient)\'/%3E%3C/clipPath%3E%3ClinearGradient id=\'linear-gradient-3\' x1=\'0.196\' y1=\'0.5\' x2=\'-0.298\' y2=\'0.5\' xlink:href=\'%23linear-gradient\'/%3E%3ClinearGradient id=\'linear-gradient-4\' x1=\'0.237\' y1=\'0.5\' x2=\'0.503\' y2=\'0.5\' xlink:href=\'%23linear-gradient\'/%3E%3C/defs%3E%3Cg id=\'Group_2\' data-name=\'Group 2\' transform=\'translate(0 -3796.621)\'%3E%3Crect id=\'Rectangle_1\' data-name=\'Rectangle 1\' width=\'907.087\' height=\'907.087\' transform=\'translate(0 3796.621)\' fill=\'url(%23linear-gradient)\'/%3E%3Cg id=\'Group_1\' data-name=\'Group 1\' clip-path=\'url(%23clip-path)\'%3E%3Cpath id=\'Path_1\' data-name=\'Path 1\' d=\'M1921.905 2783.693l-272.5-190.805L559.022 4150.119 2116.252 5240.5 2307.057 4968 1022.326 4068.426Z\' fill=\'url(%23linear-gradient-3)\'/%3E%3Cpath id=\'Path_2\' data-name=\'Path 2\' d=\'M1873.11 1690.318l-538.47-377.041L-820.018 4390.447 2257.15 6545.1l377.041-538.47L95.494 4229.018Z\' fill=\'url(%23linear-gradient-4)\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}>
            {redirect && <Navigate to="/"/>}
            <div style={{background: '#FFFFFF', boxShadow: '0px 2px 0px rgba(0, 0, 0, 0.05)', padding: '16px 32px', display: 'flex', flexDirection: width > 600 ? 'row' : 'column', alignItems: 'center', justifyContent:'space-between'}}>
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: width > 600 ? 0 : 30}}>
                    <Logo  name="latitude" color="primary.blue.t100"/>
                    <div style={{marginLeft: 24}}>
                        {
                            width > 600 ?
                                <Text as="h5" textStyle="heading5" color="primary.blue.t100">Training Module</Text>
                                :
                                <Text as="h6" textStyle="heading6" color="primary.blue.t100">Training Module</Text>
                        }
                    </div>
                    {
                        width <= 600 && width > 280 &&
                        <div style={{cursor: "pointer", marginRight: 20, marginLeft: width > 400 ? 20 : 0}} onClick={()=>props.logout()}>
                            <Icon name="logout" color="primary.blue.t100" />
                        </div>
                    }
                </div>
                { width <= 600 && <Divider style={{marginBlock:10}}/> }
                <div style={{justifySelf: 'flex-end', display: 'flex', flexDirection: 'row'}}>
                    { props.authDetails && props.authDetails.firstName && renderName() }
                    {
                        (width > 600 || width <= 280) &&
                        <div style={{cursor: "pointer", marginRight: 20}} onClick={()=>props.logout()}>
                            <Icon name="logout" color="primary.blue.t100" />
                        </div>
                    }
                </div>
            </div>
            <div style={{display:'flex', paddingBlock: 40, justifyContent: 'center', flexDirection: 'column'}}>
                {
                    modules.map((module) => (
                        <TrainingModule
                            key={module}
                            module={module}
                            title={SECTION_TITLES[module] || ''}
                            width={width}
                            alreadyCompleted={isModuleComplete(module)}
                            merchantId={merchantId}
                        />
                    ))
                }
            </div>
        </div>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(Training);
